import React, { useEffect, useState, useMemo } from 'react';
import { MdSearch, MdLocationOn, MdBusiness, MdLayers, MdMeetingRoom, MdPeople, MdEventAvailable, MdCalendarToday, MdCheckCircle, MdAccessTime, MdBlock } from 'react-icons/md';
import PageHeader from '../../admin/components/PageHeader';
import BookingModal from '../../admin/components/BookingModal';
import { conferenceRoomApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import '../../admin/Admin.css';
import './UserBookRoom.css';

// Icon components for room types
const getRoomIcon = (roomType) => {
  const icons = {
    'MEETING ROOM': <MdMeetingRoom />,
    'CONFERENCE ROOM': <MdBusiness />,
    'MEDICAL ROOM': <MdMeetingRoom />,
    'BOARD ROOM': <MdBusiness />,
    'TRAINING ROOM': <MdMeetingRoom />,
    'default': <MdMeetingRoom />
  };
  return icons[roomType?.toUpperCase()] || icons.default;
};

// Get room availability status based on bookings
const getRoomAvailability = (room) => {
  if (!room.latestBookings || room.latestBookings.length === 0) {
    return { status: 'available', label: 'Available', color: 'green', icon: <MdCheckCircle /> };
  }

  const now = new Date();
  const currentBooking = room.latestBookings.find(booking => {
    const start = new Date(booking.startTime);
    const end = new Date(booking.endTime);
    return now >= start && now <= end;
  });

  if (currentBooking) {
    const endTime = new Date(currentBooking.endTime);
    const minutesUntilFree = Math.round((endTime - now) / 60000);
    return {
      status: 'occupied',
      label: `Busy (${minutesUntilFree}m)`,
      color: 'red',
      icon: <MdBlock />,
      nextAvailable: endTime
    };
  }

  // Check if there's a booking starting soon (within 30 mins)
  const upcomingBooking = room.latestBookings.find(booking => {
    const start = new Date(booking.startTime);
    const minutesUntilStart = Math.round((start - now) / 60000);
    return minutesUntilStart > 0 && minutesUntilStart <= 30;
  });

  if (upcomingBooking) {
    const startTime = new Date(upcomingBooking.startTime);
    const minutesUntilStart = Math.round((startTime - now) / 60000);
    return {
      status: 'soon',
      label: `${minutesUntilStart}m left`,
      color: 'yellow',
      icon: <MdAccessTime />
    };
  }

  return { status: 'available', label: 'Available', color: 'green', icon: <MdCheckCircle /> };
};

// Calculate booking duration
const getBookingDuration = (startTime, endTime) => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const minutes = Math.round((end - start) / 60000);

  if (minutes < 60) {
    return `${minutes}min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;
  return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
};

// Check if booking belongs to current user
const isMyBooking = (booking) => {
  const userEmail = localStorage.getItem('userEmail');
  return booking.userEmail === userEmail;
};

// User view for booking conference rooms with location/tower/floor filters
function UserBookRoom() {
  const [rooms, setRooms] = useState([]);
  const [filters, setFilters] = useState({ query: '', location: '', tower: '', floor: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);

  useEffect(() => {
    loadRooms();
  }, []);

  const loadRooms = async () => {
    try {
      setIsLoading(true);
      const data = await conferenceRoomApi.getAllWithBookings();
      const arr = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      // Only show active rooms for booking
      const activeRooms = arr.filter(room => room.status === 'ACTIVE');
      setRooms(activeRooms);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      toast.error('Failed to load workspaces', {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get unique values for filter dropdowns
  const uniqueLocations = useMemo(() => {
    const locations = [...new Set(rooms.map(room => room.location).filter(Boolean))];
    return locations.sort();
  }, [rooms]);

  const uniqueTowers = useMemo(() => {
    let towersToShow = rooms;
    if (filters.location) {
      towersToShow = rooms.filter(room => room.location === filters.location);
    }
    const towers = [...new Set(towersToShow.map(room => room.tower).filter(Boolean))];
    return towers.sort();
  }, [rooms, filters.location]);

  const uniqueFloors = useMemo(() => {
    let floorsToShow = rooms;
    if (filters.location) {
      floorsToShow = floorsToShow.filter(room => room.location === filters.location);
    }
    if (filters.tower) {
      floorsToShow = floorsToShow.filter(room => room.tower === filters.tower);
    }
    const floors = [...new Set(floorsToShow.map(room => room.floor).filter(Boolean))];
    return floors.sort();
  }, [rooms, filters.location, filters.tower]);

  const filteredRooms = useMemo(() => {
    const q = (filters.query || '').toLowerCase().trim();
    return rooms.filter((room) => {
      const name = (room.roomName || '').toLowerCase();
      const type = (room.roomType || '').toLowerCase();
      const location = (room.location || '').toLowerCase();
      const tower = (room.tower || '').toLowerCase();
      const floor = (room.floor || '').toLowerCase();

      const matchesSearch = !q || name.includes(q) || type.includes(q) || location.includes(q) || tower.includes(q) || floor.includes(q);
      const matchesLocation = !filters.location || room.location === filters.location;
      const matchesTower = !filters.tower || room.tower === filters.tower;
      const matchesFloor = !filters.floor || room.floor === filters.floor;

      return matchesSearch && matchesLocation && matchesTower && matchesFloor;
    });
  }, [rooms, filters]);

  const openBookingModal = (room) => {
    setSelectedRoom(room);
    setShowBookingModal(true);
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setSelectedRoom(null);
  };

  const handleBookingSuccess = () => {
    toast.success('Booking request submitted successfully!', {
      position: "top-right",
      autoClose: 3000,
    });
    closeBookingModal();
    // Reload rooms to show updated latest bookings
    loadRooms();
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };

      // Reset dependent filters when parent filter changes
      if (field === 'location') {
        newFilters.tower = '';
        newFilters.floor = '';
      } else if (field === 'tower') {
        newFilters.floor = '';
      }

      return newFilters;
    });
  };

  const handleResetFilters = () => {
    setFilters({ query: '', location: '', tower: '', floor: '' });
  };

  // Get user initials for avatar
  const getInitials = (email) => {
    if (!email) return '?';
    return email.charAt(0).toUpperCase();
  };

  return (
    <div className="admin-view book-room-view">
      {/* Combined Header and Filters Row */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdCalendarToday className="title-icon" />
            Book a Workspace
          </h2>
        </div>

        <div className="header-filters-compact">
          <div className="input search-compact">
            <span className="icon"><MdSearch /></span>
            <input
              placeholder="Search workspaces..."
              value={filters.query}
              onChange={(e) => handleFilterChange('query', e.target.value)}
            />
          </div>

          <select
            value={filters.location}
            onChange={(e) => handleFilterChange('location', e.target.value)}
            className="input select-compact"
          >
            <option value="">Location</option>
            {uniqueLocations.map(loc => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>

          <select
            value={filters.tower}
            onChange={(e) => handleFilterChange('tower', e.target.value)}
            className="input select-compact"
            disabled={!filters.location}
          >
            <option value="">Tower</option>
            {uniqueTowers.map(tower => (
              <option key={tower} value={tower}>{tower}</option>
            ))}
          </select>

          <select
            value={filters.floor}
            onChange={(e) => handleFilterChange('floor', e.target.value)}
            className="input select-compact"
            disabled={!filters.tower}
          >
            <option value="">Floor</option>
            {uniqueFloors.map(floor => (
              <option key={floor} value={floor}>{floor}</option>
            ))}
          </select>

          <button onClick={handleResetFilters} className="btn-reset-compact">
            Reset
          </button>
        </div>
      </div>

      {/* Workspaces List - Modern Table Layout */}
      <div className="rooms-list-container">
        {isLoading && (
          <div className="loading-state">
            <div className="loading-spinner-large"></div>
            <p>Loading workspaces...</p>
          </div>
        )}

        {!isLoading && filteredRooms.length > 0 && (
          <div className="rooms-list">
            {filteredRooms.map((room) => {
              const availability = getRoomAvailability(room);

              return (<div key={room.id} className="room-row">
                {/* Room Icon & Name */}
                <div className="room-main-info">
                  <div className="room-icon-wrapper">
                    <span className="room-icon">{getRoomIcon(room.roomType)}</span>
                    {/* Availability indicator dot */}
                    <span className={`availability-dot availability-${availability.color}`}></span>
                  </div>
                  <div className="room-name-section">
                    <div className="room-name-row">
                      <h3 className="room-name">{room.roomName}</h3>
                      <span className={`availability-badge availability-badge-${availability.color}`}>
                        {availability.icon}
                        {availability.label}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Room Details */}
                <div className="room-details-section">
                  <div className="room-detail-item">
                    <span className="detail-icon"><MdLocationOn /></span>
                    <span className="detail-value">{room.location}</span>
                  </div>
                  <div className="room-detail-item">
                    <span className="detail-icon"><MdBusiness /></span>
                    <span className="detail-value">{room.tower}</span>
                  </div>
                  <div className="room-detail-item">
                    <span className="detail-icon"><MdLayers /></span>
                    <span className="detail-value">{room.floor}</span>
                  </div>
                  {room.capacity && (
                    <div className="room-detail-item">
                      <span className="detail-icon"><MdPeople /></span>
                      <span className="detail-value">{room.capacity} people</span>
                    </div>
                  )}
                  {/* Next Available Info for Occupied/Soon Rooms */}
                  {availability.nextAvailable && (
                    <div className="room-detail-item next-available-info">
                      <span className="detail-icon"><MdEventAvailable /></span>
                      <span className="detail-value">
                        Free at {new Date(availability.nextAvailable).toLocaleString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  )}
                </div>

                {/* Upcoming Bookings */}
                <div className="room-bookings-section">
                  {room.latestBookings && room.latestBookings.length > 0 ? (
                    <div className="bookings-compact">
                      {room.latestBookings.slice(0, 2).map((booking) => {
                        const isMine = isMyBooking(booking);
                        const duration = getBookingDuration(booking.startTime, booking.endTime);

                        return (<div key={booking.bookingId} className={`booking-chip ${isMine ? 'my-booking' : ''}`} title={`Booking ID: ${booking.bookingId}`}>
                          <span className="booking-id-badge">{(booking.bookingId || '').toString().slice(0, 6)}</span>
                          <div className="booking-avatar-small">{getInitials(booking.userEmail)}</div>
                          <span className="booking-info">
                            {isMine && <span className="my-booking-label">You</span>}
                            {!isMine && booking.userEmail.split('@')[0]} • {new Date(booking.startTime).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })} • <span className="booking-duration">{duration}</span>
                          </span>
                        </div>);
                      })}
                      {room.latestBookings.length > 2 && (
                        <span className="more-bookings">+{room.latestBookings.length - 2} more</span>
                      )}
                    </div>
                  ) : (
                    <span className="no-bookings">No upcoming bookings</span>
                  )}
                </div>

                {/* Book Button */}
                <div className="room-action-section">
                  <button
                    className="btn-book-compact"
                    onClick={() => openBookingModal(room)}
                    title="Book this workspace"
                  >
                    <MdCalendarToday />
                    <span>Book</span>
                  </button>
                </div>
              </div>);
            })}
          </div>
        )}

        {!isLoading && filteredRooms.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3>No Workspaces Found</h3>
            <p>
              {filters.query || filters.location || filters.tower || filters.floor
                ? 'Try adjusting your filters to see more results'
                : 'There are currently no active workspaces available'}
            </p>
            {(filters.query || filters.location || filters.tower || filters.floor) && (
              <button onClick={handleResetFilters} className="empty-action">
                Clear All Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Booking Modal */}
      <BookingModal
        isOpen={showBookingModal}
        onClose={closeBookingModal}
        selectedRoom={selectedRoom}
        onBookingSuccess={handleBookingSuccess}
        isUserView={true}
      />
    </div>
  );
}

export default UserBookRoom;
